export const RESOURCES = {
  DASHBOARD: "dashboard",
  JOBS: "job",
  CANDIDATE: "candidate",
  ROLE: "role",
  USER: "user",
  INTERVIEW: "interview",
  NOTE: "note",
  REPORT: "report",
  BRANDING: "branding",
  FEEDBACK: "feedback",
  CANDIDATE_STATUS: "candidate_status",
  TENANT_CONFIG: "tenant_config",
  STATISTICS: "statistics",
  COMPLIANCE: "compliance",
  OUTREACH: "outreach",
  RESUME_TEMPLATE: "resume_template",
  USAGE: "usage",
  EXTENSION: "extension",
  // Sourcing — the backend resource is `campaign` (not `sourcing`); the
  // Campaigns and Shortlist pages both gate on it.
  CAMPAIGN: "campaign",
  SUPER_ADMIN: "super-admin",
  RECORD: "record",

  // All
  ALL: "*",
};

export const ACTIONS = {
  VIEW: "view",
  CREATE: "create",
  UPDATE: "update",
  EDIT: "edit",
  DELETE: "delete",
  ASSIGN: "assign",
  MANAGE: "manage",
  REVIEW: "review",

  ALL: "*",
};

const VIEW_ALL = "view_all";
const EXPORT = "export";

export const PERMISSIONS = {
  DASHBOARD_VIEW: `${RESOURCES.DASHBOARD}.${ACTIONS.VIEW}`,

  JOB_VIEW: `${RESOURCES.JOBS}.${ACTIONS.VIEW}`,
  JOB_VIEW_ALL: `${RESOURCES.JOBS}.${VIEW_ALL}`,
  JOB_CREATE: `${RESOURCES.JOBS}.${ACTIONS.CREATE}`,
  JOB_UPDATE: `${RESOURCES.JOBS}.${ACTIONS.UPDATE}`,
  JOB_DELETE: `${RESOURCES.JOBS}.${ACTIONS.DELETE}`,
  JOB_ASSIGN: `${RESOURCES.JOBS}.${ACTIONS.ASSIGN}`,

  CANDIDATE_VIEW: `${RESOURCES.CANDIDATE}.${ACTIONS.VIEW}`,
  CANDIDATE_VIEW_ALL: `${RESOURCES.CANDIDATE}.${VIEW_ALL}`,
  CANDIDATE_CREATE: `${RESOURCES.CANDIDATE}.${ACTIONS.CREATE}`,
  CANDIDATE_UPDATE: `${RESOURCES.CANDIDATE}.${ACTIONS.UPDATE}`,
  CANDIDATE_DELETE: `${RESOURCES.CANDIDATE}.${ACTIONS.DELETE}`,
  // Connectivity Phase F. The Compliance tab on the candidate page, and the
  // downloads inside it. Inline literals rather than `ACTIONS` entries — that
  // object holds the generic verbs, and these are compound actions, the same
  // shape as RECORD_EXPORT and USAGE_VIEW_USERS below.
  //
  // Distinct from COMPLIANCE_VIEW, which gates the compliance dashboard.
  // Neither implies the other.
  CANDIDATE_VIEW_COMPLIANCE: `${RESOURCES.CANDIDATE}.view_compliance`,
  CANDIDATE_DOWNLOAD_DOCUMENTS: `${RESOURCES.CANDIDATE}.download_documents`,
  // Reopening a Hired or Rejected application: raising the request, and
  // deciding one. `candidate.update` covers neither.
  CANDIDATE_UNDO_REQUEST: `${RESOURCES.CANDIDATE}.undo_request`,
  CANDIDATE_UNDO_REVIEW: `${RESOURCES.CANDIDATE}.undo_review`,

  // Kept — deliberately excluded from Interview Scheduling below. Also
  // gates the unrelated candidate-profile "Reupload/Edit" actions and a
  // legacy feedback route, so it survives Interview Scheduling being
  // disabled. See PLAN doc "resilient-stirring-tulip".
  INTERVIEW_MANAGE: `${RESOURCES.INTERVIEW}.${ACTIONS.MANAGE}`,

  // Interview Scheduling is disabled — commented out, not deleted. See
  // PLAN doc "resilient-stirring-tulip".
  // // Interview Scheduling (new module — mirrors server/app/core/permissions.py Perm.INTERVIEW_*)
  // INTERVIEW_SCHEDULE: `${RESOURCES.INTERVIEW}.schedule`,
  // INTERVIEW_RESCHEDULE: `${RESOURCES.INTERVIEW}.reschedule`,
  // INTERVIEW_CANCEL: `${RESOURCES.INTERVIEW}.cancel`,
  // INTERVIEW_VIEW: `${RESOURCES.INTERVIEW}.${ACTIONS.VIEW}`,
  // INTERVIEW_VIEW_LINK: `${RESOURCES.INTERVIEW}.view_link`,
  // INTERVIEW_REASSIGN: `${RESOURCES.INTERVIEW}.reassign`,
  // INTERVIEW_QUEUE_MANAGE: `${RESOURCES.INTERVIEW}.queue_manage`,
  // // Edit the module's own config (org-settings -> Interview Scheduling
  // // Defaults). Delegatable on its own, so an admin can hand interview
  // // config to whoever owns scheduling without granting every org setting.
  // INTERVIEW_CONFIG_EDIT: `${RESOURCES.INTERVIEW}.config_edit`,

  // Candidate notes. Their own pair rather than a reuse of `candidate.view` /
  // `candidate.update`. Mirror the backend
  // Perm.NOTE_* strings (server/app/core/permissions.py).
  NOTE_VIEW: `${RESOURCES.NOTE}.${ACTIONS.VIEW}`,
  NOTE_MANAGE: `${RESOURCES.NOTE}.${ACTIONS.MANAGE}`,

  REPORT_VIEW: `${RESOURCES.REPORT}.${ACTIONS.VIEW}`,

  // Admin Statistics — diagnostic recruitment analytics.
  // Mirror the backend Perm.STATISTICS_VIEW string (server/app/core/permissions.py).
  STATISTICS_VIEW: `${RESOURCES.STATISTICS}.${ACTIONS.VIEW}`,

  ROLE_VIEW: `${RESOURCES.ROLE}.${ACTIONS.VIEW}`,
  ROLE_CREATE: `${RESOURCES.ROLE}.${ACTIONS.CREATE}`,
  ROLE_UPDATE: `${RESOURCES.ROLE}.${ACTIONS.UPDATE}`,
  ROLE_DELETE: `${RESOURCES.ROLE}.${ACTIONS.DELETE}`,
  ROLE_MANAGE: `${RESOURCES.ROLE}.${ACTIONS.MANAGE}`,

  USER_VIEW: `${RESOURCES.USER}.${ACTIONS.VIEW}`,
  USER_VIEW_ALL: `${RESOURCES.USER}.${VIEW_ALL}`,
  USER_CREATE: `${RESOURCES.USER}.${ACTIONS.CREATE}`,
  USER_UPDATE: `${RESOURCES.USER}.${ACTIONS.UPDATE}`,
  USER_DELETE: `${RESOURCES.USER}.${ACTIONS.DELETE}`,

  OUTREACH_VIEW: `${RESOURCES.OUTREACH}.${ACTIONS.VIEW}`,
  OUTREACH_MANAGE: `${RESOURCES.OUTREACH}.${ACTIONS.MANAGE}`,
  OUTREACH_SEND: `${RESOURCES.OUTREACH}.send`,
  BRANDING_VIEW: `${RESOURCES.BRANDING}.${ACTIONS.VIEW}`,
  BRANDING_MANAGE: `${RESOURCES.BRANDING}.${ACTIONS.MANAGE}`,
  FEEDBACK_SUBMIT: `${RESOURCES.FEEDBACK}.submit`,

  // Also covers Shift Timing (predefined shift configuration) — it's a
  // card inside Organization Settings, not a separate feature, so it
  // reuses this pair rather than having its own view/manage codes.
  TENANT_CONFIG_VIEW: `${RESOURCES.TENANT_CONFIG}.${ACTIONS.VIEW}`,
  TENANT_CONFIG_EDIT: `${RESOURCES.TENANT_CONFIG}.${ACTIONS.EDIT}`,

  CANDIDATE_STATUS_VIEW: `${RESOURCES.CANDIDATE_STATUS}.${ACTIONS.VIEW}`,
  CANDIDATE_STATUS_CREATE: `${RESOURCES.CANDIDATE_STATUS}.${ACTIONS.CREATE}`,
  CANDIDATE_STATUS_UPDATE: `${RESOURCES.CANDIDATE_STATUS}.${ACTIONS.UPDATE}`,
  CANDIDATE_STATUS_DELETE: `${RESOURCES.CANDIDATE_STATUS}.${ACTIONS.DELETE}`,

  SUPER_ADMIN_VIEW: `${RESOURCES.SUPER_ADMIN}.${ACTIONS.VIEW}`,
  SUPER_ADMIN_CREATE: `${RESOURCES.SUPER_ADMIN}.${ACTIONS.CREATE}`,
  SUPER_ADMIN_UPDATE: `${RESOURCES.SUPER_ADMIN}.${ACTIONS.UPDATE}`,
  SUPER_ADMIN_DELETE: `${RESOURCES.SUPER_ADMIN}.${ACTIONS.DELETE}`,
  SUPER_ADMIN_MANAGE: `${RESOURCES.SUPER_ADMIN}.${ACTIONS.MANAGE}`,

  // CV Formatting is disabled — commented out, not deleted. See PLAN doc
  // "resilient-stirring-tulip".
  //
  // // CV formatting — one code covers listing, previewing and exporting a
  // // formatted CV. Mirror the backend Perm.RESUME_TEMPLATE_EXPORT string
  // // (server/app/core/permissions.py).
  // RESUME_TEMPLATE_EXPORT: `${RESOURCES.RESUME_TEMPLATE}.${EXPORT}`,

  // Compliance.
  // Mirror the backend Perm.COMPLIANCE_* strings (server/app/core/permissions.py).
  COMPLIANCE_VIEW: `${RESOURCES.COMPLIANCE}.${ACTIONS.VIEW}`,
  COMPLIANCE_CREATE: `${RESOURCES.COMPLIANCE}.${ACTIONS.CREATE}`,
  COMPLIANCE_REVIEW: `${RESOURCES.COMPLIANCE}.${ACTIONS.REVIEW}`,
  COMPLIANCE_MANAGE: `${RESOURCES.COMPLIANCE}.${ACTIONS.MANAGE}`,
  // The Audit Log page. Its own code rather than COMPLIANCE_VIEW: the trail is
  // tenant-wide with no data-visibility narrowing, so an administrator grants
  // it deliberately to whoever is meant to read everything. Literal action
  // string, the way `usage.view_users` is — ACTIONS has no compound entry.
  COMPLIANCE_AUDIT_VIEW: `${RESOURCES.COMPLIANCE}.audit_view`,
  // A literal rather than an ACTIONS entry: "master_manage" is specific to
  // compliance and adding it to the shared action vocabulary would offer it on
  // every other resource. Separate from COMPLIANCE_MANAGE because a country
  // document set is the source every checklist for that country starts from —
  // a tenant can let recruiters build checklists without letting them change
  // that. Reading a master needs only COMPLIANCE_VIEW.
  COMPLIANCE_MASTER_MANAGE: `${RESOURCES.COMPLIANCE}.master_manage`,

  // Usage metering. Mirror the backend Perm.USAGE_* strings
  // (server/app/core/permissions.py). Two codes, not one: `usage.view_users`
  // is separate so a lead can be given tenant totals without also seeing who
  // did what.
  USAGE_VIEW: `${RESOURCES.USAGE}.${ACTIONS.VIEW}`,
  USAGE_VIEW_USERS: `${RESOURCES.USAGE}.view_users`,

  // Record lookup — the GDPR / EU AI Act dossier.
  // Two codes: `record.view` opens search and the whole dossier; `record.export`
  // opens the download, and only ever with `record.view` also held (the backend
  // checks both). No module code — a withheld module must never block a tenant
  // answering a regulator.
  RECORD_VIEW: `${RESOURCES.RECORD}.${ACTIONS.VIEW}`,
  RECORD_EXPORT: `${RESOURCES.RECORD}.export`,

  // Extension and Sourcing are disabled — commented out, not deleted. See
  // PLAN doc "resilient-stirring-tulip".
  //
  // // Extension (LinkedIn Chrome extension). Mirror the backend Perm.EXTENSION_*
  // // strings (server/app/core/permissions.py).
  // //
  // // ONE flag only. There was an EXTENSION_MANAGE gating the extension's two
  // // write routes; it was removed because capturing profiles IS the point of the
  // // extension, so VIEW-without-MANAGE produced a user who could install it but
  // // not use it. Anyone who can see the extension can use it.
  // EXTENSION_VIEW: `${RESOURCES.EXTENSION}.${ACTIONS.VIEW}`,
  //
  // // Sourcing (Campaigns + Shortlist). Mirror the backend Perm.CAMPAIGN_*
  // // strings (server/app/core/permissions.py). RUN has no ACTIONS entry
  // // because it's sourcing-specific — it gates starting a campaign fetch or a
  // // scrape job, which spends shared external API quota.
  // //
  // // MANAGE gates the "New Campaign" button (and campaign edit/delete,
  // // shortlist, reveal, enrich, convert). RUN gates "Create & Start" on the new
  // // campaign form and the Start / Run Again buttons on a campaign.
  // CAMPAIGN_VIEW: `${RESOURCES.CAMPAIGN}.${ACTIONS.VIEW}`,
  // CAMPAIGN_MANAGE: `${RESOURCES.CAMPAIGN}.${ACTIONS.MANAGE}`,
  // CAMPAIGN_RUN: `${RESOURCES.CAMPAIGN}.run`,
  //
  // // Gates the "Web Scraper" tab inside a campaign and every scraper route.
  // // Separate from RUN so the crawler — the piece with third-party ToS and
  // // rate-limit exposure — can be withheld from users who may still run a
  // // normal platform-API fetch.
  // CAMPAIGN_SCRAPER_MANAGE: `${RESOURCES.CAMPAIGN}.scraper_manage`,
  //
  // // Gates the tenant-facing "Configure APIs" screen, where a tenant stores and
  // // rotates its OWN sourcing platform API keys. Separate from CAMPAIGN_MANAGE
  // // on purpose: handling live billable credentials is a higher privilege than
  // // editing a campaign row, so campaign editors do not get it implicitly.
  // //
  // // Note this permission alone is not enough — the tenant must ALSO be set to
  // // bring its own keys by a SuperAdmin (`sourcingUsesOwnApiKeys` on
  // // /auth/current-user). The API enforces both; hiding the nav item is only a
  // // convenience.
  // CAMPAIGN_CREDENTIALS_MANAGE: `${RESOURCES.CAMPAIGN}.credentials_manage`,

  ALL: "*",
};
